import React, { useMemo } from "react";
import { isString, isUndefined } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { Label } from "../../../../../../Inputs";
import { Text } from "../../../../../../Inputs/react-hook-form";
import TextInput from "../../../../../../Inputs/Text";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  InputFieldPath,
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
} from "./shared";
import { TextDynamicDefaultInput } from "./shared";

const TextWidgetSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const { register, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();
  const isFormulaField = !!watch(`${fieldPath}.formula`);

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={isFormulaField}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <div>
        <Label text={"Placeholder text"} htmlFor={`${fieldPath}.placeholder`} />
        <Text {...register(`${fieldPath}.placeholder`)} disabled={readOnly} />
      </div>
      {!isFormulaField && (
        <div>
          <Label
            text={"Unique identifier"}
            htmlFor={`${fieldPath}.uniqueIdentifier`}
          />
          <Text
            {...register(`${fieldPath}.uniqueIdentifier`)}
            disabled={readOnly}
          />
        </div>
      )}
      <DefaultValuesSection showTooltip>
        <FieldWrapper data-testid="dynamic-default-value-wrapper">
          <TextDynamicDefaultInput fieldPath={fieldPath} readOnly={readOnly} />
        </FieldWrapper>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <TextStaticDefaultInput fieldPath={fieldPath} readOnly={readOnly} />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

export const TextStaticDefaultInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValue = watch(`${fieldPath}.defaultValue`);

  const value = useMemo<string | null>(() => {
    if (isString(defaultValue) || isUndefined(defaultValue)) {
      return defaultValue || "";
    }
    throw new Error(`defaultValue must be a string or undefined`);
  }, [defaultValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <TextInput
            value={value}
            label="Static default value"
            name={`${fieldPath}.defaultValue`}
            onChange={value => {
              // store no value (undefined) for ui:defaultValue instead of empty string in form state
              field.onChange(value || undefined);
            }}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

TextWidgetSettings.displayName = "TextWidgetSettings";

export default TextWidgetSettings;
